import React, { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";
import { Link, useHistory } from "react-router-dom";
import {
  FaArrowLeft,
  FaFolderPlus,
  FaSave,
  FaImage,
  FaCloudUploadAlt,
  FaTrashAlt,
  FaCheckCircle,
  FaEye,
  FaLayerGroup,
  FaInfoCircle
} from "react-icons/fa";
import ShopifySEOEditor from "../common/ShopifySEOEditor";
import { checkImageFile, focusFirstError, hasApiErrors, readApiErrors } from "../../../util/formErrors";

export default function AddCategory() {
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);

  const [categoryInput, setCategoryInput] = useState({
    name: "",
    slug: "",
    descrip: "",
    status: true, // true = active/visible (1), false = hidden/draft (0)
    meta_title: "",
    meta_tag: "",
    meta_desc: "",
    error_list: {},
  });

  const [picture, setPicture] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isCustomSlug, setIsCustomSlug] = useState(false);

  const slugify = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[\s\W-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleInput = (e) => {
    e.persist();
    const { name, value } = e.target;

    setCategoryInput((prev) => {
      // Typing in a field clears its error.
      const updated = { ...prev, [name]: value, error_list: { ...prev.error_list, [name]: "", ...(name === "name" ? { slug: "" } : {}) } };
      if (name === "name" && !isCustomSlug) {
        updated.slug = slugify(value);
      }
      return updated;
    });
  };

  const handleSlugChange = (e) => {
    setIsCustomSlug(true);
    setCategoryInput((prev) => ({
      ...prev,
      slug: slugify(e.target.value)
    }));
  };

  const setFieldError = (field, message) => setCategoryInput((prev) => ({ ...prev, error_list: { ...prev.error_list, [field]: message } }));

  // Check the photo the moment it is chosen, with the same rules as the server.
  const handleImage = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const problem = checkImageFile(file);
    if (problem) {
      setFieldError("image", problem);
      return;
    }
    setFieldError("image", "");
    setPicture(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setPicture(null);
    setImagePreview(null);
  };

  const submitCategory = (e) => {
    e.preventDefault();

    if (!categoryInput.name.trim()) {
      setFieldError("name", "Enter a category name.");
      focusFirstError();
      return;
    }

    setSubmitting(true);
    const finalSlug = categoryInput.slug || slugify(categoryInput.name);
    const finalMetaTitle = categoryInput.meta_title || categoryInput.name;

    const formData = new FormData();
    if (picture) {
      formData.append("image", picture);
    }
    formData.append("slug", finalSlug);
    formData.append("name", categoryInput.name);
    formData.append("description", categoryInput.descrip || "");
    formData.append("status", categoryInput.status ? 1 : 0);
    formData.append("meta_title", finalMetaTitle);
    formData.append("meta_keyword", categoryInput.meta_tag || "");
    formData.append("meta_descrip", categoryInput.meta_desc || "");

    // Errors arrive as a 200 reply with status 400/422, or as an HTTP error: show both under the fields.
    const showErrors = (source) => {
      const { message, fields } = readApiErrors(source);
      setCategoryInput((prev) => ({ ...prev, error_list: fields }));
      focusFirstError();
      swal("Category not saved", message || "Please check the highlighted fields.", "error");
    };
    axios.post(`/api/store-category`, formData).then((res) => {
      setSubmitting(false);
      if (res.data.status === 200) {
        swal("Success", res.data.message || "Category created successfully", "success");
        history.push("/admin/view-category");
      } else if (hasApiErrors(res)) {
        showErrors(res);
      }
    }).catch((err) => {
      setSubmitting(false);
      showErrors(err);
    });
  };

  useEffect(() => {
    document.title = "Add Category — Admin Management";
  }, []);

  return (
    <div className="container-fluid px-0">
      {/* Top Header Bar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1 small">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard" className="text-decoration-none text-muted">
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/view-category" className="text-decoration-none text-muted">
                  Categories
                </Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                Add Category
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Add Category
            </h3>
            <span className="admin-badge admin-badge-info">
              <FaLayerGroup size={11} className="me-1" /> Taxonomy Catalog
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/view-category"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaArrowLeft size={13} /> Discard
          </Link>
          <button
            type="button"
            onClick={submitCategory}
            disabled={submitting}
            className="btn btn-primary d-inline-flex align-items-center gap-2 fw-semibold px-4 py-2 shadow-sm rounded-3"
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FaSave size={14} /> Save Category
              </>
            )}
          </button>
        </div>
      </div>

      <form onSubmit={submitCategory} id="CATEGORY_FORM">
        <div className="row g-4">
          {/* Left Column: Primary Category Info */}
          <div className="col-12 col-lg-8">
            {/* Category Details Card */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <FaFolderPlus className="text-primary" />
                <h5 className="admin-card-title mb-0">Category Information</h5>
              </div>
              <div className="admin-card-body p-4">
                {/* Category Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g. Women's Handbags, Winter Jackets"
                    className={`form-control form-control-lg ${categoryInput.error_list?.name ? "is-invalid" : ""}`}
                    onChange={handleInput}
                    value={categoryInput.name}
                    required
                  />
                  {categoryInput.error_list?.name && (
                    <div className="invalid-feedback">{categoryInput.error_list.name}</div>
                  )}
                </div>

                {/* Slug Handle */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-semibold text-dark mb-0 small">
                      URL Slug Handle
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsCustomSlug(!isCustomSlug)}
                      className="btn btn-link btn-sm text-decoration-none p-0"
                      style={{ fontSize: "12px" }}
                    >
                      {isCustomSlug ? "Auto-generate" : "Edit URL"}
                    </button>
                  </div>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted small font-monospace">
                      /collections/
                    </span>
                    <input
                      type="text"
                      name="slug"
                      disabled={!isCustomSlug}
                      placeholder="womens-handbags"
                      className={`form-control font-monospace ${categoryInput.error_list?.slug ? "is-invalid" : ""}`}
                      onChange={handleSlugChange}
                      value={categoryInput.slug || slugify(categoryInput.name)}
                    />
                  </div>
                  {categoryInput.error_list?.slug && <small className="text-danger d-block mt-1">{categoryInput.error_list.slug}</small>}
                  <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>
                    The “slug” is the URL-friendly version of the name. It is usually all lowercase and contains only letters, numbers, and hyphens.
                  </small>
                </div>

                {/* Description */}
                <div className="mb-0">
                  <label className="form-label fw-semibold text-dark">Description</label>
                  <textarea
                    name="descrip"
                    rows="4"
                    placeholder="Add a detailed description for this category to improve discovery..."
                    className="form-control"
                    onChange={handleInput}
                    value={categoryInput.descrip}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted" style={{ fontSize: "11.5px" }}>
                      This description will be displayed on the category collection page.
                    </small>
                    <small className="text-muted" style={{ fontSize: "11.5px" }}>
                      {categoryInput.descrip.length} characters
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Banner & Image Card */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FaImage className="text-success" />
                  <h5 className="admin-card-title mb-0">Category Image / Banner</h5>
                </div>
                <span className="badge bg-light text-muted border">Optional</span>
              </div>
              <div className="admin-card-body p-4">
                {!imagePreview ? (
                  <label
                    htmlFor="category-image-upload"
                    className="d-flex flex-column align-items-center justify-content-center p-5 rounded-3 border-2 border-dashed bg-light text-center cursor-pointer w-100 mb-0"
                    style={{ borderStyle: "dashed", borderColor: "#cbd5e1", cursor: "pointer", transition: "all 0.2s" }}
                  >
                    <FaCloudUploadAlt size={48} className="text-primary mb-2 opacity-75" />
                    <span className="fw-bold text-dark fs-6 mb-1">Click to upload category banner</span>
                    <span className="text-muted small mb-2">JPG, PNG, WebP or GIF (max 5 MB)</span>
                    <span className="btn btn-sm btn-outline-primary px-3 rounded-pill">Select Image</span>
                    <input
                      id="category-image-upload"
                      type="file"
                      name="image"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="d-none"
                      onChange={handleImage}
                    />
                  </label>
                ) : (
                  <div className="position-relative rounded-3 overflow-hidden border p-2 bg-light">
                    <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                      <span className="fw-semibold text-dark small d-flex align-items-center gap-1.5">
                        <FaCheckCircle className="text-success" size={13} /> Selected Category Image
                      </span>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 py-0 px-2 rounded-2"
                        style={{ fontSize: "11.5px" }}
                      >
                        <FaTrashAlt size={11} /> Remove
                      </button>
                    </div>
                    <div
                      className="rounded-3 overflow-hidden bg-white d-flex align-items-center justify-content-center"
                      style={{ height: "220px" }}
                    >
                      <img
                        src={imagePreview}
                        alt="Category Preview"
                        style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                      />
                    </div>
                    <div className="text-muted text-center mt-2 small" style={{ fontSize: "11.5px" }}>
                      {picture?.name} • {(picture?.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                )}
                {categoryInput.error_list?.image && (
                  <div className="field-error alert alert-danger py-2 px-3 mt-2 mb-0 small" role="alert">{categoryInput.error_list.image}</div>
                )}
              </div>
            </div>

            {/* Shopify-Style Search Engine Optimization (SEO) */}
            <ShopifySEOEditor
              itemName={categoryInput.name}
              itemCategoryName={categoryInput.name}
              itemDescription={categoryInput.descrip}
              itemSlug={categoryInput.slug}
              metaTitle={categoryInput.meta_title}
              metaDescription={categoryInput.meta_desc}
              metaKeywords={categoryInput.meta_tag}
              type="category"
              onChange={(updated) => {
                setCategoryInput((prev) => ({
                  ...prev,
                  meta_title: updated.meta_title !== undefined ? updated.meta_title : prev.meta_title,
                  meta_desc: updated.meta_descrip !== undefined ? updated.meta_descrip : prev.meta_desc,
                  meta_tag: updated.meta_keyword !== undefined ? updated.meta_keyword : prev.meta_tag,
                }));
              }}
            />
          </div>

          {/* Right Column: Visibility & Sidebar Widgets */}
          <div className="col-12 col-lg-4">
            {/* Publishing & Visibility Card */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <FaEye className="text-primary" />
                <h5 className="admin-card-title mb-0">Publishing Status</h5>
              </div>
              <div className="admin-card-body p-4">
                <div className="form-check form-switch mb-3 d-flex align-items-center justify-content-between ps-0">
                  <div>
                    <label
                      htmlFor="category-status-switch"
                      className="form-check-label fw-semibold text-dark d-block mb-0 cursor-pointer"
                    >
                      Store Visibility
                    </label>
                    <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                      {categoryInput.status ? "Visible to customers in store (Active / Shown)" : "Hidden from customer storefront (Hidden)"}
                    </small>
                  </div>
                  <input
                    id="category-status-switch"
                    type="checkbox"
                    className="form-check-input ms-2 cursor-pointer"
                    style={{ width: "2.8em", height: "1.4em", cursor: "pointer" }}
                    checked={Boolean(categoryInput.status)}
                    onChange={(e) =>
                      setCategoryInput((prev) => ({
                        ...prev,
                        status: e.target.checked,
                      }))
                    }
                  />
                </div>

                <div className="p-3 rounded-3 bg-light border">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small text-muted">Status:</span>
                    {categoryInput.status ? (
                      <span className="admin-badge admin-badge-success">
                        <FaCheckCircle size={10} className="me-1" /> Active & Visible
                      </span>
                    ) : (
                      <span className="admin-badge admin-badge-warning">
                        Hidden / Draft
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Guidelines Card */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <FaInfoCircle className="text-warning" />
                <h5 className="admin-card-title mb-0">Merchandising Tips</h5>
              </div>
              <div className="admin-card-body p-4 small text-muted">
                <ul className="ps-3 mb-0 d-flex flex-column gap-2" style={{ fontSize: "12.5px" }}>
                  <li>
                    <strong className="text-dark">Descriptive Names:</strong> Keep names concise and familiar to customers.
                  </li>
                  <li>
                    <strong className="text-dark">Clear Imagery:</strong> High-resolution horizontal banners look best on collection pages.
                  </li>
                  <li>
                    <strong className="text-dark">Search Optimization:</strong> Add relevant keywords to meta tags to increase organic traffic.
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Actions Card */}
            <div className="admin-card shadow-sm border-0">
              <div className="admin-card-body p-3 d-flex flex-column gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary d-inline-flex align-items-center justify-content-center gap-2 fw-semibold py-2.5 shadow-sm rounded-3 w-100"
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FaSave size={14} /> Save Category
                    </>
                  )}
                </button>
                <Link
                  to="/admin/view-category"
                  className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2 py-2 rounded-3 w-100"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
